import { useEffect, useState, ChangeEvent } from "react";
import axios from "axios";
import { API_BASE_PATH } from "../../environment";
import { toast } from "react-toastify";

export interface BankFormData {
  id?: string; // table id (for update)
  employee_id?: string;
  bank_account_name: string;
  bank_account_no: string; // keep as string in UI, but cast to number on submit
  MICR: string;
  IFSC: string;
  branch_name: string;
  branch_address: string;
}

const BANK_BASE_URL = `${API_BASE_PATH}/bank-info`;

const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message ;
};

export const useBankInfo = (
  employeeId: string | null,
  token: string | null
) => {
  const [formData, setFormData] = useState<BankFormData>({
    bank_account_name: "",
    bank_account_no: "",
    MICR: "",
    IFSC: "",
    branch_name: "",
    branch_address: "",
  });

  const getBankInfo = async () => {
    if (!employeeId || !token) return;
    try {
      const res = await axios.get(`${BANK_BASE_URL}/${employeeId}`, {
        headers: {
          Accept: "application/json",
          Authorization: "Bearer " + token,
        },
      });
      const data = res.data;
      if (Array.isArray(data) && data.length > 0) {
        setFormData({
          ...data[0], // or whichever record you want to display/edit
          bank_account_no: String(data[0].bank_account_no ?? ""),
        });
      } else {
        console.warn(`No bank record found for employee ID ${employeeId}.`);
        setFormData({
          bank_account_name: "",
          bank_account_no: "",
          MICR: "",
          IFSC: "",
          branch_name: "",
          branch_address: "",
        });
      }
    } catch (err: any) {
      const message = extractErrorMessage(err);
      if (err.response?.status === 404) {
        console.warn(`Bank info not found for employee ID ${employeeId}.`);
      } else {
        console.error("Error fetching bank info:", message);
        toast.error(message);
      }
    }
  };

  useEffect(() => {
    getBankInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, token]);

  const handleChangeBank = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateBankForm = (data: BankFormData): string | null => {
    if (!data.bank_account_name.trim()) return "Bank account name is required.";
    if (!data.bank_account_no.trim()) return "Bank account number is required.";
    if (!data.MICR.trim()) return "MICR is required.";
    if (!data.IFSC.trim()) return "IFSC is required.";
    if (!data.branch_name.trim()) return "Branch name is required.";
    if (!data.branch_address.trim()) return "Branch address is required.";
    return null;
  };

  const handleSubmitBank = async (onSuccess?: () => void) => {
    if (!employeeId) {
      toast.warn("Employee ID is missing.");
      return;
    }
    if (!token) {
      toast.warn("Authentication token is missing.");
      return;
    }

    const validationError = validateBankForm(formData);
    if (validationError) {
      toast.warn(validationError);
      return;
    }

    try {
      const payload = {
        bank_account_name: formData.bank_account_name,
        bank_account_no: Number(formData.bank_account_no), // ✅ ensure integer
        MICR: formData.MICR,
        IFSC: formData.IFSC,
        branch_name: formData.branch_name,
        branch_address: formData.branch_address,
        employee_id: String(employeeId),
      };

      if (formData.id) {
        // update existing record
        await axios.put(`${BANK_BASE_URL}/${formData.id}`, payload, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });
        toast.success("Bank information updated successfully.");
      } else {
        // create new record
        await axios.post(`${BANK_BASE_URL}`, payload, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });
        toast.success("Bank information created successfully.");
      }

      await getBankInfo();
      if (onSuccess) {
        onSuccess(); // <-- parent can close modal here
      } else {
        handleLocalReset(); // <-- fallback: clear form if no callback provided
      }
    } catch (error: any) {
      const message = extractErrorMessage(error);
      console.error("❌ Error submitting bank info:", message);
      toast.error(`Failed to save bank information: ${message}`);
    }
  };

  const handleLocalReset = () => {
    setFormData({
      bank_account_name: "",
      bank_account_no: "",
      MICR: "",
      IFSC: "",
      branch_name: "",
      branch_address: "",
    });
  };

  return {
    formData,
    handleChangeBank,
    handleSubmitBank,
    getBankInfo,
    handleLocalReset,
  };
};
